import React, { useEffect, useState } from "react";
import axiosClient from "../api/axiosClient";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import "../styles/DashboardPage.css";

// ─── Custom Tooltip ───────────────────────────────────────────────────────────
const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="gp-tooltip">
      <div className="gp-tooltip-label">{label}</div>
      <div className="gp-tooltip-value">{payload[0].value}</div>
    </div>
  );
};

// ─── Compute stats from data array ───────────────────────────────────────────
function computeStats(data) {
  if (!data || data.length === 0) return { total: 0, avg: 0, trend: null };
  const values = data.map((d) => d.value ?? 0);
  const total = values.reduce((a, b) => a + b, 0);
  const avg = Math.round(total / values.length);
  // const first = values[0];
  // const last = values[values.length - 1];
  // const trend = first === 0 ? null : Math.round(((last - first) / first) * 100);
  return { total, avg};
}

// ─── Tab config ───────────────────────────────────────────────────────────────
const CONFIG = {
  days:   { arrayKey: "last7Days",    xKey: "dayName",   label: "Last 7 Days" },
  weeks:  { arrayKey: "last7Weeks",   xKey: "weekLabel", label: "Last 7 Weeks" },
  months: { arrayKey: "last12Months", xKey: "monthName", label: "Last 12 Months" },
};

// ─── Main Component ───────────────────────────────────────────────────────────
export default function GraphPopup({ showGraphPopup, setShowGraphPopup }) {
  const [graphData, setGraphData] = useState(null);
  const [type, setType] = useState("days");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchGraphData = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem("admintoken");
      const res = await axiosClient.get("/auth/admin/userExamDetails",
         {
        headers: { 
          Authorization: `${token}`
         },
      });

      console.log("GRAPH DATA", res.data);
      setGraphData(res.data);
    } catch (err) {
      console.error(err);
      setError("Could not load data. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (showGraphPopup) fetchGraphData();
  }, [showGraphPopup]);

  const { arrayKey, xKey, label } = CONFIG[type];
  const data = graphData?.[arrayKey] || [];
  const { total, avg} = computeStats(data);

  if (!showGraphPopup) return null;

  return (
    <>
      {/* Overlay */}
      <div className="gp-overlay" onClick={() => setShowGraphPopup(false)} />

      {/* Popup */}
      <div className="gp-popup" onClick={(e) => e.stopPropagation()}>

        {/* Header */}
        <div className="gp-header">
          <div>
            <h2 className="gp-title">User Performance</h2>
            <p className="gp-subtitle">{label} · exam activity</p>
          </div>
          <button className="gp-close" onClick={() => setShowGraphPopup(false)}>
            ×
          </button>
        </div>

        {/* Tab switcher */}
        <div className="gp-tabs">
          {Object.entries(CONFIG).map(([key, cfg]) => (
            <button
              key={key}
              className={`gp-tab${type === key ? " active" : ""}`}
              onClick={() => setType(key)}
            >
              {cfg.label}
            </button>
          ))}
        </div>

        {/* Stats row — only when data is available */}
        {!loading && data.length > 0 && (
          <div className="gp-stats">
            <div className="gp-stat">
              <div className="gp-stat-label">Total</div>
              <div className="gp-stat-value">{total.toLocaleString()}</div>
            </div>
            <div className="gp-stat">
              <div className="gp-stat-label">Average</div>
              <div className="gp-stat-value">{avg.toLocaleString()}</div>
            </div>
            {/* {trend !== null && (
              <div className="gp-stat">
                <div className="gp-stat-label">Overall</div>
                <div className={`gp-stat-value ${trend >= 0 ? "up" : "down"}`}>
                  {trend >= 0 ? "+" : ""}{trend}%
                </div>
              </div>
            )} */}
          </div>
        )}
        {/* Chart */}
        {loading ? (
          <div className="gp-loading">
            <div className="gp-spinner" />
          </div>
        ) : error ? (
          <div className="gp-empty">
            <span className="gp-empty-icon">⚠️</span>
            <span>{error}</span>
          </div>
        ) : data.length === 0 ? (
          <div className="gp-empty">
            <span className="gp-empty-icon">📊</span>
            <span>No data for this period</span>
          </div>
        ) : (
          <div className="gp-chart-wrap">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart
                data={data}
                margin={{ top: 4, right: 8, left: -16, bottom: 0 }}
              >
                <defs>
                  <linearGradient id="gp-line-glow" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%"   stopColor="#2563eb" stopOpacity={0.15} />
                    <stop offset="100%" stopColor="#2563eb" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid
                  strokeDasharray="4 4"
                  stroke="#f1f5f9"
                  vertical={false}
                />
                <XAxis
                  dataKey={xKey}
                  tick={{ fontFamily: "DM Sans", fontSize: 12, fill: "#94a3b8" }}
                  axisLine={false}
                  tickLine={false}
                  dy={8}
                />
                <YAxis
                  tick={{ fontFamily: "DM Mono", fontSize: 11, fill: "#94a3b8" }}
                  axisLine={false}
                  tickLine={false}
                  dx={-4}
                />
                <Tooltip
                  content={<CustomTooltip />}
                  cursor={{ stroke: "#e2e8f0", strokeWidth: 1 }}
                />
                <Line
                  type="monotone"
                  dataKey="value"
                  stroke="#2563eb"
                  strokeWidth={2.5}
                  dot={{ r: 4, fill: "#fff", strokeWidth: 2.5, stroke: "#2563eb" }}
                  activeDot={{ r: 6, fill: "#2563eb", stroke: "#fff", strokeWidth: 2 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Footer */}
        <div className="gp-footer">
          {/* <button
            className="gp-btn-cancel"
            onClick={() => setShowGraphPopup(false)}
          >
            Close
          </button> */}
          <button
            className="gp-btn-refresh"
            onClick={fetchGraphData}
            disabled={loading}
          >
            {loading ? "Loading…" : "↻ Refresh"}
          </button>
        </div>
      </div>
    </>
  );
}